import {
  Badge,
  Card,
  CardContent,
  Stack,
  Skeleton,
} from '@hurdl/design-system';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';

import { type ReviewItem } from '../bespoke/menu-workspace/types.js';
import { restaurantLabelOf, useMenuOperations } from './menus-wiring.js';

/**
 * The Review_Queue as a list (menus-spec R13): every item awaiting review, each a row
 * that opens the focused single-item review.
 *
 * This is the list alone — no editor. Each row names the item (truncated so a long name
 * never pushes the row wide), the Restaurant it belongs to as a `Badge`, and why it is
 * queued; clicking it goes to the focused review of that one item. The count beside the
 * heading is a `Badge`, primary while any wait. Placeholders stand in while the read is
 * in flight, and an empty queue says so rather than showing nothing.
 *
 * The one `h1` is the page's; the `main` landmark is the Layout's. The rows' links
 * resolve against the route tree, so no address literal reaches this source.
 */
export function ReviewQueuePage(): ReactElement {
  const { productKey } = useParams({ strict: false });
  const operations = useMenuOperations();
  const [queue, setQueue] = useState<ReadonlyArray<ReviewItem> | undefined>(
    undefined,
  );

  useEffect(() => {
    let live = true;
    void operations.listQueue().then((items) => {
      if (live) setQueue(items);
    });
    return () => {
      live = false;
    };
  }, [operations]);

  return (
    <Stack gap="large">
      <Stack direction="horizontal" gap="small" align="centre">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          Review queue
        </h1>
        {queue !== undefined ? (
          <Badge tone={queue.length > 0 ? 'primary' : 'neutral'}>
            {queue.length}
          </Badge>
        ) : undefined}
      </Stack>

      {queue === undefined ? (
        <Stack gap="small" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </Stack>
      ) : queue.length === 0 ? (
        <p className="text-hurdl-muted-foreground">Nothing awaiting review.</p>
      ) : (
        <Stack gap="small">
          {queue.map((item) => (
            <QueueRow
              key={item.itemKey}
              item={item}
              productKey={productKey ?? ''}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** One queued item as a linked row: name, restaurant, reason. */
function QueueRow({
  item,
  productKey,
}: {
  readonly item: ReviewItem;
  readonly productKey: string;
}): ReactElement {
  const reason = item.reason === 'new-item' ? 'New item' : 'Allergen change';

  return (
    <Link
      to="/products/$productKey/review/$itemKey"
      params={{ productKey, itemKey: item.itemKey }}
      className="block rounded-hurdl-large outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card className="transition-colors hover:bg-accent">
        <CardContent>
          <Stack
            direction="responsive"
            gap="medium"
            align="centre"
            className="justify-between"
          >
            <span className="min-w-0 truncate text-hurdl-subheading font-hurdl-semibold">
              {item.extracted.name}
            </span>
            <Stack direction="horizontal" gap="small" align="centre">
              <Badge tone="neutral">
                {restaurantLabelOf(item.restaurantKey)}
              </Badge>
              <Badge
                tone={item.reason === 'new-item' ? 'informational' : 'warning'}
              >
                {reason}
              </Badge>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Link>
  );
}
